import { NgClass } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    ElementRef,
    forwardRef,
    HostBinding,
    HostListener,
    inject,
    input,
    OnChanges,
    output,
    signal,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    ControlValueAccessor,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import { Icon } from '../../icon/icon.component';
import { DocumentService } from '../../../services/document.service';
import { computeConnectedPosition } from '../../../services/overlay-position';
import { multiSelectFade } from './multi-select-input.animation';

export type MultiSelectInputOption = {
    label: string;
    value: string;
    selected?: boolean;
    data?: any;
};

/** Lado de abertura do painel de opções. */
export type MultiSelectOpenDirection = 'auto' | 'top' | 'bottom';

/**
 * Exibe um componente estilo um SELECT, mas que permite a múltipla
 * seleção de itens. Os itens são exibidos com um checkbox ao lado.
 *
 * Recursos: chips dos selecionados (com colapso em "+N"), busca interna
 * automática (quando há muitas opções), posicionamento inteligente do painel
 * (nunca abre fora da tela), fechamento ao clicar fora / Esc, navegação por
 * teclado e cores 100% do tema.
 */
@Component({
    selector: 'ai-multi-select-input',
    standalone: true,
    imports: [NgClass, Icon, FormsModule, ReactiveFormsModule],
    templateUrl: './multi-select-input.component.html',
    styleUrls: ['./multi-select-input.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    animations: [multiSelectFade],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiSelectInput),
            multi: true,
        },
    ],
})
export class MultiSelectInput implements ControlValueAccessor, OnChanges {
    @HostBinding('attr.tabindex')
    tabindex = 0;

    @ViewChild('selection')
    protected selectionElement!: ElementRef<HTMLElement>;

    @ViewChild('optionBox')
    protected optionBoxElement?: ElementRef<HTMLElement>;

    private readonly eRef = inject(ElementRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly documentService = inject(DocumentService);

    /** Texto exibido quando nenhuma opção está selecionada. */
    public placeholder = input<string | null>(null);
    /** Rótulo da opção "selecionar todos" (quando definido, exibe a opção). */
    public selectAllOptionLabel = input<string | null>(null);
    /** Opções disponíveis para seleção (obrigatório). */
    public options = input.required<MultiSelectInputOption[]>();
    /** Lado preferido para abrir o painel. */
    public openDirection = input<MultiSelectOpenDirection>('auto');
    /** A busca aparece quando o número de opções excede este limiar. */
    public searchThreshold = input<number>(8);
    /** Quando verdadeiro, exibe sempre a caixa de busca, independente do número de opções. */
    public alwaysShowSearch = input<boolean>(false);
    /** Placeholder da caixa de busca. */
    public searchPlaceholder = input<string>('Pesquisar');
    /** Mensagem exibida quando a busca não retorna resultados. */
    public emptyText = input<string>('Nenhum resultado');
    /** Quantidade máxima de chips antes de colapsar em "+N". */
    public maxChips = input<number>(2);

    /** Desabilita o componente via binding `[disabled]`. */
    protected readonly disabledInput = input<boolean>(false, {
        alias: 'disabled',
    });
    /** Desabilitação vinda do Form (CVA, via `setDisabledState`). */
    private readonly cvaDisabled = signal<boolean>(false);
    /** Estado efetivo: desabilitado por input OU pelo Form. */
    public readonly disabled = computed(
        () => this.disabledInput() || this.cvaDisabled(),
    );

    /** Emite o termo de busca digitado pelo usuário a cada alteração no campo de pesquisa. */
    public searchChange = output<string>();

    protected optionsVisible = signal<boolean>(false);
    protected searchTerm = signal<string>('');
    /** Índice (em `filteredOptions`) do item ativo para navegação por teclado. */
    protected activeIndex = signal<number>(-1);

    // Posição do painel (coordenadas de viewport — usadas com position: fixed).
    protected panelTop = signal<number>(0);
    protected panelLeft = signal<number>(0);
    protected panelWidth = signal<number>(0);
    protected panelMaxHeight = signal<number>(0);

    /** Lista de selecionados (signal reativo, atualizado imperativamente). */
    protected selectedOptions = signal<MultiSelectInputOption[]>([]);

    protected selectAllOption = computed(
        (): MultiSelectInputOption | undefined => {
            if (this.selectAllOptionLabel()) {
                return {
                    label: this.selectAllOptionLabel()!,
                    value: '<ALL>',
                    selected: false,
                } as MultiSelectInputOption;
            }
            return undefined;
        },
    );

    /** Opções visíveis após aplicar o filtro de busca. */
    protected filteredOptions = computed((): MultiSelectInputOption[] => {
        const term = this.normalize(this.searchTerm());
        if (!term) {
            return this.options();
        }
        return this.options().filter((option) =>
            this.normalize(option.label).includes(term),
        );
    });

    /** Indica se a caixa de busca deve ser exibida. */
    protected showSearch = computed(
        () => this.alwaysShowSearch() || this.options().length > this.searchThreshold(),
    );

    /** Chips a exibir no gatilho (limitados por `maxChips`). */
    protected chips = computed(() =>
        this.selectedOptions().slice(0, this.maxChips()),
    );

    /** Quantidade de selecionados além dos chips exibidos. */
    protected extraCount = computed(() =>
        Math.max(0, this.selectedOptions().length - this.maxChips()),
    );

    protected hasSelection = computed(() => this.selectedOptions().length > 0);

    // Guarda a lista de selecionados para ser utilizada quando
    // a lista de opções for atualizada (writeValue pode chegar antes).
    private lastWriteValue: MultiSelectInputOption[] = [];
    // Indica se o valor já foi escrito por um Form (CVA). Sem isso, respeitamos
    // o `selected` que vier nas próprias opções (uso sem formulário).
    private hasWrittenValue = false;
    private onChanged: (value: MultiSelectInputOption[]) => void = () => {};
    private onTouched: () => void = () => {};

    /** Handler de reposicionamento (scroll/resize) enquanto o painel está aberto. */
    private readonly repositionHandler = (): void => {
        if (this.optionsVisible()) {
            this.updatePanelPosition();
        }
    };

    constructor() {
        // Fecha o painel ao clicar fora do componente ou ao pressionar Esc.
        this.documentService
            .onDocumentClick()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => {
                if (
                    this.optionsVisible() &&
                    event &&
                    !this.eRef.nativeElement.contains(event.target)
                ) {
                    this.close();
                }
            });

        this.documentService
            .onKeyDownEscape()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.optionsVisible()) {
                    this.close();
                }
            });

        this.destroyRef.onDestroy(() => this.removeRepositionListeners());
    }

    /**
     * Implementa o método Hook do OnChanges.
     * Re-sincroniza a seleção quando a lista de opções é (re)carregada.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['options']) {
            this.synchronizeWithLastWriteValue();
        }
    }

    // ---- ControlValueAccessor ------------------------------------------------

    writeValue(value: MultiSelectInputOption[]): void {
        this.lastWriteValue = value ?? [];
        this.hasWrittenValue = true;
        this.synchronizeWithLastWriteValue();
    }

    registerOnChange(fn: (value: MultiSelectInputOption[]) => void): void {
        this.onChanged = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.cvaDisabled.set(isDisabled);
        if (isDisabled) {
            this.close();
        }
    }

    // ---- Abertura/fechamento e posicionamento --------------------------------

    /**
     * Alterna a visibilidade do painel. Ignora quando desabilitado.
     */
    protected changeVisibility(): void {
        if (this.disabled()) {
            return;
        }
        if (this.optionsVisible()) {
            this.close();
        } else {
            this.open();
        }
    }

    private open(): void {
        this.searchTerm.set('');
        this.activeIndex.set(-1);
        this.optionsVisible.set(true);
        this.onTouched();
        this.addRepositionListeners();
        // O painel só existe no DOM após o próximo frame (renderizado por @if).
        requestAnimationFrame(() => this.updatePanelPosition());
    }

    private close(): void {
        this.optionsVisible.set(false);
        this.activeIndex.set(-1);
        this.removeRepositionListeners();
    }

    private addRepositionListeners(): void {
        window.addEventListener('scroll', this.repositionHandler, true);
        window.addEventListener('resize', this.repositionHandler);
    }

    private removeRepositionListeners(): void {
        window.removeEventListener('scroll', this.repositionHandler, true);
        window.removeEventListener('resize', this.repositionHandler);
    }

    /** Recalcula a posição do painel a partir do retângulo do gatilho. */
    private updatePanelPosition(): void {
        const trigger = this.selectionElement?.nativeElement;
        const panel = this.optionBoxElement?.nativeElement;
        if (!trigger || !panel) {
            return;
        }

        const rect = trigger.getBoundingClientRect();
        const position = computeConnectedPosition(
            {
                top: rect.top,
                bottom: rect.bottom,
                left: rect.left,
                width: rect.width,
            },
            { width: rect.width, height: panel.scrollHeight },
            { width: window.innerWidth, height: window.innerHeight },
            { preferred: this.openDirection() },
        );

        this.panelTop.set(position.top);
        this.panelLeft.set(position.left);
        this.panelWidth.set(position.width);
        this.panelMaxHeight.set(position.maxHeight);
    }

    // ---- Seleção -------------------------------------------------------------

    /**
     * Trata o clique em um dos checkboxes da lista.
     *
     * @param option Opção que foi clicada.
     */
    onCheckboxChanged(option: MultiSelectInputOption): void {
        option.selected = !option.selected;
        this.afterSelectionChanged();
    }

    /**
     * Alterna a opção "selecionar todos": marca/desmarca todas as opções.
     */
    protected onAllSelected(): void {
        const isSelected = !this.selectAllOption()?.selected;
        this.selectAllOption()!.selected = isSelected;
        this.options().forEach((option) => (option.selected = isSelected));
        this.afterSelectionChanged();
    }

    /**
     * Remove um chip (desmarca a opção correspondente) sem abrir/fechar o painel.
     *
     * @param option Opção a desmarcar.
     * @param event Evento de clique (para impedir a propagação ao gatilho).
     */
    protected removeChip(option: MultiSelectInputOption, event: Event): void {
        event.stopPropagation();
        if (this.disabled()) {
            return;
        }
        option.selected = false;
        this.afterSelectionChanged();
    }

    /**
     * Limpa toda a seleção. Não abre nem fecha o painel.
     *
     * @param event Evento de clique (para impedir a propagação ao gatilho).
     */
    protected clearAll(event: Event): void {
        event.stopPropagation();
        if (this.disabled()) {
            return;
        }
        this.options().forEach((option) => (option.selected = false));
        const all = this.selectAllOption();
        if (all) {
            all.selected = false;
        }
        this.afterSelectionChanged();
    }

    /**
     * Recalcula derivados após qualquer mudança de seleção e notifica o Form.
     */
    private afterSelectionChanged(): void {
        this.verifyAllCheckboxesAreSelected();
        this.refreshSelection();
        this.emitOnChange();
    }

    private emitOnChange(): void {
        this.onChanged(this.options().filter((option) => option.selected));
    }

    private refreshSelection(): void {
        this.selectedOptions.set(
            this.options().filter((option) => option.selected),
        );
    }

    /**
     * Sincroniza o estado "selected" das opções com o último valor recebido
     * via `writeValue()`.
     */
    private synchronizeWithLastWriteValue(): void {
        // Com Form, o valor escrito é a fonte da verdade. Sem Form, respeita-se
        // o `selected` já presente nas opções (permite pré-seleção declarativa).
        if (this.hasWrittenValue) {
            this.options().forEach((option) => {
                option.selected = this.lastWriteValue.some(
                    (v) => v.value === option.value,
                );
            });
        }
        this.verifyAllCheckboxesAreSelected();
        this.refreshSelection();
    }

    /**
     * Mantém a opção "selecionar todos" coerente com o estado das demais.
     */
    private verifyAllCheckboxesAreSelected(): void {
        const all = this.selectAllOption();
        if (all) {
            const selected = this.options().filter((o) => o.selected).length;
            all.selected = this.options().length === selected;
        }
    }

    // ---- Teclado -------------------------------------------------------------

    /**
     * Navegação por teclado a partir do componente (host focável).
     */
    @HostListener('keydown', ['$event'])
    protected onTriggerKeydown(event: KeyboardEvent): void {
        if (this.disabled()) {
            return;
        }

        const key = event.key;
        // Eventos vindos da caixa de busca: preservar a digitação (espaço, etc.).
        const fromSearchField =
            (event.target as HTMLElement)?.tagName === 'INPUT';

        if (!this.optionsVisible()) {
            if (key === 'Enter' || key === ' ' || key === 'ArrowDown') {
                event.preventDefault();
                this.open();
            }
            return;
        }

        switch (key) {
            case 'Escape':
                event.preventDefault();
                this.close();
                break;
            case 'ArrowDown':
                event.preventDefault();
                this.moveActive(1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.moveActive(-1);
                break;
            case ' ':
                // Na busca, espaço é digitação normal.
                if (fromSearchField) {
                    return;
                }
                event.preventDefault();
                this.toggleActiveOption();
                break;
            case 'Enter':
                event.preventDefault();
                this.toggleActiveOption();
                break;
        }
    }

    private toggleActiveOption(): void {
        const option = this.filteredOptions()[this.activeIndex()];
        if (option) {
            this.onCheckboxChanged(option);
        }
    }

    private moveActive(delta: number): void {
        const total = this.filteredOptions().length;
        if (total === 0) {
            this.activeIndex.set(-1);
            return;
        }
        const next = (this.activeIndex() + delta + total) % total;
        this.activeIndex.set(next);
    }

    /** Atualiza o termo de busca, reposiciona o painel e notifica os consumidores. */
    protected onSearchInput(value: string): void {
        this.searchTerm.set(value);
        this.activeIndex.set(-1);
        this.searchChange.emit(value);
        requestAnimationFrame(() => this.updatePanelPosition());
    }

    /**
     * Combina marcas diacríticas (acentos) — faixa Unicode U+0300–U+036F.
     * Construído via string para evitar problemas de codificação no fonte.
     */
    private static readonly DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

    /** Normaliza texto para busca: minúsculas e sem acentos. */
    private normalize(text: string): string {
        return (text ?? '')
            .toLowerCase()
            .normalize('NFD')
            .replace(MultiSelectInput.DIACRITICS, '');
    }
}
